// Normalizes Better Days live-backend responses into the shapes the UI expects.
import { FALLBACK_RANGES } from "@/lib/fallbackRanges";

export function normalizeUser(raw) {
  if (!raw) return raw;
  const u = raw.user || raw;
  if (!u) return u;
  return {
    ...u,
    id: u.user_id || u.id,
    name: u.name || "Member",
    email: u.email,
    date_of_birth: u.dob || u.date_of_birth || "",
    picture: u.picture || null,
    goal: u.goal || "Optimize my health",
    plan: u.plan || (u.is_active_mentor ? "Mentor" : "Member"),
    streak: u.streak ?? 0,
    is_shop_admin: !!u.is_shop_admin,
    is_active_mentor: !!u.is_active_mentor,
    has_completed_setup: u.has_completed_setup,
  };
}

export function normalizeProduct(p) {
  if (!p) return p;
  let image = p.image_url || p.hero_image || p.image || (p.images && p.images[0]) || "";
  // Mobile-app convention: "placeholder:<icon>" means no photo — render a
  // branded placeholder on the web instead of a broken image.
  if (typeof image === "string" && image.startsWith("placeholder:")) image = "";
  return {
    slug: p.slug,
    name: p.name,
    tagline: p.subtitle || p.tagline || "",
    price: p.price_gbp ?? p.price ?? 0,
    currency: p.currency || "GBP",
    image,
    category: p.category,
    subcategory: p.subcategory || p.sub_category || null,
    description: p.description || "",
    benefits: p.benefits || p.highlights || p.bullets || [],
    in_stock: p.in_stock !== false,
    servings: p.servings || "",
    dosage: p.dosage || "",
  };
}

export function unwrap(data, key) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return data ?? [];
}

const COMPONENT_META = {
  bloodwork: { name: "Blood Markers", link: "/app/bloodwork" },
  bp: { name: "Blood Pressure", link: "/app/dashboard" },
  body_comp: { name: "Body Composition", link: "/app/settings" },
  nutrition: { name: "Nutrition", link: "/app/meals" },
  activity: { name: "Activity", link: "/app/workouts" },
  sleep: { name: "Sleep", link: "/app/dashboard" },
  hydration: { name: "Hydration", link: "/app/dashboard" },
};

export function normalizeDashboard(data, activityHistory) {
  if (!data) return { health_score: 0, components: [], trend: [], attention: [], vitals: null, streak: 0 };
  let components = [];
  if (Array.isArray(data.components)) {
    components = data.components;
  } else if (data.components && typeof data.components === "object") {
    components = Object.entries(data.components).map(([key, val]) => {
      const score = typeof val === "number" ? val
        : (val && typeof val.score === "number" ? val.score : null);
      const meta = COMPONENT_META[key] || { name: key, link: "/app/dashboard" };
      return { name: meta.name, score, link: meta.link };
    });
  }
  const scored = components.filter((c) => typeof c.score === "number");

  // Vitals — the live backend nests these; flatten to what the cards expect.
  let vitals = null;
  if (data.vitals || data.activity || data.activity_breakdown) {
    const vc = (data.vitals && data.vitals.components) || {};
    const ab = data.activity_breakdown || {};
    const act = data.activity || {};
    const steps = (act.steps_today && act.steps_today > 0) ? act.steps_today : (act.steps_7d_avg ?? act.steps_today ?? null);
    vitals = {
      hrv: vc.hrv?.value ?? null,
      rhr: vc.rhr?.value ?? data.bp?.latest?.pulse ?? null,
      sleep: ab.sleep?.hours ?? null,
      steps: steps != null ? Math.round(steps) : null,
    };
  }

  // Readiness trend — daily activity/readiness score series (last 14 days).
  let trend = [];
  const series = activityHistory && Array.isArray(activityHistory.series) ? activityHistory.series : null;
  if (series) {
    trend = series
      .filter((p) => p && typeof p.score === "number")
      .slice(-14)
      .map((p) => ({ date: fmtShortDate(p.date), readiness: Math.round(p.score) }));
  } else if (Array.isArray(data.trend)) {
    trend = data.trend;
  }

  // Needs attention — real shape is { marker, value, unit, target, priority, priority_color, category }.
  let attention = [];
  if (Array.isArray(data.attention) && data.attention.length) {
    attention = data.attention.map((a) => ({
      name: a.marker || a.name || a.label || a.category || "Marker",
      value: a.value != null ? `${a.value}${a.unit ? ` ${a.unit}` : ""}` : null,
      target: a.target || null,
      priority: a.priority || null,
      priorityColor: a.priority_color || "warning",
      category: a.category || null,
      recommendation: a.recommendation || null,
      link: "/app/bloodwork",
    }));
  } else {
    attention = scored.filter((c) => c.score < 75).map((c) => ({ name: c.name, score: c.score, link: c.link }));
  }

  return {
    health_score: data.health_score ?? data.health_score_raw ?? 0,
    score_label: data.score_label || null,
    components,
    component_weights: data.component_weights || null,
    hasScoredComponents: scored.length > 0,
    trend,
    vitals,
    streak: data.streak ?? 0,
    attention,
  };
}

function fmtShortDate(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return String(d); }
}

// ---------------------------------------------------------------------------
// Bloodwork — tolerant of multiple live shapes
// ---------------------------------------------------------------------------
function statusFor(v, low, high) {
  if (low == null && high == null) return null;
  if (high != null && v > high) return "out";
  if (low != null && v < low) return "out";
  const lo = low ?? high, hi = high ?? low;
  if (hi != null && lo != null && hi !== lo) {
    const m = (hi - lo) * 0.12;
    if (v <= lo + m || v >= hi - m) return "watch";
  }
  return "optimal";
}

export function buildMarkerDefs(arr) {
  const map = {};
  (Array.isArray(arr) ? arr : []).forEach((m) => {
    const def = {
      unit: m.unit || "",
      low: m.range_low ?? null,
      high: m.range_high ?? null,
      about: m.what_it_measures || m.description || "",
      category: m.category || "General",
      sexRanges: m.sex_ranges || null,
      ageRanges: Array.isArray(m.age_ranges) ? m.age_ranges : null,
      scaleMin: m.scale_min ?? null,
      scaleMax: m.scale_max ?? null,
      fallback: false,
    };
    // The backend leaves some markers without any reference range — fill those in
    // from our curated fallback so they still get a range + R/A/G status.
    if (def.low == null && def.high == null && !def.sexRanges && !def.ageRanges) {
      const fb = FALLBACK_RANGES[m.marker];
      if (fb) {
        def.low = fb.low ?? null;
        def.high = fb.high ?? null;
        def.sexRanges = fb.sexRanges || null;
        def.scaleMin = def.scaleMin ?? fb.scaleMin ?? null;
        def.scaleMax = def.scaleMax ?? fb.scaleMax ?? null;
        def.fallback = true;
      }
    }
    map[m.marker] = def;
    (m.aliases || []).forEach((a) => { if (!map[a]) map[a] = def; });
  });
  return map;
}

// Mirrors the live backend's effective-range resolution so the table matches the
// drill-down modal exactly: age_ranges (optionally sex-scoped) → sex_ranges → default.
function ageBandContext(r) {
  const band = (r.max_age == null || r.max_age >= 200)
    ? `Age ${r.min_age}+`
    : `Age ${r.min_age}\u2013${r.max_age}`;
  return r.sex ? `${band}, ${r.sex}` : band;
}

export function resolveRange(def, ctx) {
  const age = ctx?.age ?? null;
  const sex = ctx?.sex ?? null;
  const fb = !!def.fallback;
  const base = { low: def.low ?? null, high: def.high ?? null, source: "default", context: "Standard adult reference", note: null, fallback: fb };
  // 1. Age-banded ranges (highest priority), optionally scoped to a sex.
  if (age != null && Array.isArray(def.ageRanges)) {
    for (const r of def.ageRanges) {
      if (!r) continue;
      const minOk = r.min_age == null || age >= r.min_age;
      const maxOk = r.max_age == null || age <= r.max_age;
      const sexOk = !r.sex || r.sex === sex;
      if (minOk && maxOk && sexOk) {
        return { low: r.range_low ?? base.low, high: r.range_high ?? base.high,
          source: r.sex ? "age_sex" : "age", context: ageBandContext(r), note: r.note || null, fallback: fb };
      }
    }
  }
  // 2. Sex-specific ranges.
  if (sex && def.sexRanges && def.sexRanges[sex]) {
    const sr = def.sexRanges[sex];
    return { low: sr.range_low ?? base.low, high: sr.range_high ?? base.high,
      source: "sex", context: `${sex.charAt(0).toUpperCase()}${sex.slice(1)} reference`, note: sr.note || null, fallback: fb };
  }
  // 3. Standard adult default.
  return base;
}

function pushHist(store, marker, date, value) {
  if (marker == null || date == null || value == null) return;
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return;
  (store[marker] = store[marker] || []).push({ date: String(date).slice(0, 10), value: num });
}

function summarize(rows) {
  const s = { optimal: 0, watch: 0, out: 0, total: rows.length };
  rows.forEach((r) => { if (r.status) s[r.status]++; });
  return s;
}

// Change vs the previous test, made range-aware so the UI can say whether a
// marker improved (moved toward/into its optimal range) or worsened.
function computeDelta(hist, low, high) {
  if (!hist || hist.length < 2) return null;
  const latest = hist[hist.length - 1].value;
  const prev = hist[hist.length - 2].value;
  const prevDate = hist[hist.length - 2].date;
  const diff = Math.round((latest - prev) * 100) / 100;
  const badness = (v) => {
    if (low != null && v < low) return low - v;
    if (high != null && v > high) return v - high;
    return 0;
  };
  const bNow = badness(latest), bPrev = badness(prev);
  let trend = "neutral";
  if (bNow < bPrev) trend = "better";
  else if (bNow > bPrev) trend = "worse";
  return { diff, dir: diff > 0 ? "up" : diff < 0 ? "down" : "flat", trend, prevDate };
}

export function normalizeBloodwork(mh, tests, defsArr, userCtx) {
  const defs = buildMarkerDefs(defsArr);
  // Local stand-in already returns {dates, rows, summary}
  if (mh && Array.isArray(mh.rows)) {
    return { dates: mh.dates || [], rows: mh.rows, summary: mh.summary || summarize(mh.rows), userContext: userCtx || null };
  }
  const store = {};
  // Shape A: marker-histories as a dict { "Marker": [{date,value}, ...] }
  if (mh && typeof mh === "object" && !Array.isArray(mh)) {
    Object.entries(mh).forEach(([marker, arr]) => {
      const list = Array.isArray(arr) ? arr : (arr && Array.isArray(arr.history) ? arr.history : []);
      list.forEach((pt) => {
        if (pt == null) return;
        const date = pt.date || pt.measured_at || pt.collected_at || pt.taken_at || pt.test_date || pt.timestamp;
        const value = pt.value ?? pt.result ?? pt.val;
        pushHist(store, marker, date, value);
      });
    });
  }
  // Shape B: marker-histories as an array of {marker, history:[...]}
  if (Array.isArray(mh)) {
    mh.forEach((row) => {
      const marker = row.marker || row.name;
      (row.history || row.points || []).forEach((pt) =>
        pushHist(store, marker, pt.date || pt.measured_at || pt.collected_at, pt.value ?? pt.result));
    });
  }
  // Shape C: reconstruct from the tests list
  const testsArr = Array.isArray(tests) ? tests : (tests && tests.tests) || [];
  testsArr.forEach((t) => {
    const date = t.date || t.test_date || t.collected_at || t.taken_at || t.created_at || t.timestamp;
    const results = t.results || t.markers || t.values || t.data;
    if (Array.isArray(results)) {
      results.forEach((r) => pushHist(store, r.marker || r.name, r.date || date, r.value ?? r.result));
    } else if (results && typeof results === "object") {
      Object.entries(results).forEach(([m, v]) =>
        pushHist(store, m, date, v && typeof v === "object" ? (v.value ?? v.result) : v));
    }
  });

  const allDates = Array.from(new Set(Object.values(store).flat().map((p) => p.date))).sort();
  const rows = Object.keys(store).map((marker) => {
    const def = defs[marker] || {};
    const eff = resolveRange(def, userCtx);
    const low = eff.low, high = eff.high;
    const hist = store[marker].slice().sort((a, b) => (a.date < b.date ? -1 : 1));
    const byDate = {};
    hist.forEach((p) => { byDate[p.date] = p.value; });
    const cells = allDates.map((d) => ({
      date: d,
      value: byDate[d] !== undefined ? byDate[d] : null,
      status: byDate[d] !== undefined ? statusFor(byDate[d], low, high) : null,
    }));
    const latest = hist.length ? hist[hist.length - 1].value : null;
    return {
      marker, unit: def.unit || "", low: low ?? null, high: high ?? null,
      rangeContext: eff.context, rangeSource: eff.source, rangeNote: eff.note || null, rangeFallback: !!eff.fallback,
      about: def.about || "", history: hist, cells, latest,
      status: latest != null ? statusFor(latest, low, high) : null,
      delta: computeDelta(hist, low, high),
    };
  }).sort((a, b) => a.marker.localeCompare(b.marker));

  return { dates: allDates, rows, summary: summarize(rows), userContext: userCtx || null };
}
